import { getSharedState } from "./windowBus";

// Framework-agnostic request queue backing confirmDialog()/promptDialog()
// (dialogs.ts). Same split as toastBus.ts: this module stays React/MUI/
// dom.ts-free so vanilla call sites keep importing dialogs.ts with zero
// changes; only DialogHost.tsx (the React renderer, mounted once in
// AppRoot) pulls in React. Entirely windowBus-backed for the same reason
// as toastBus.ts - app.js and react-app.js are separately-bundled IIFEs
// with no shared module state (see windowBus.ts).
//
// Only one confirm/prompt can be open at a time in this app's real usage
// (always awaited sequentially, never invoked concurrently) - unlike
// toasts, which do stack - so a single `current` slot is enough and
// mirrors actual call patterns.
export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
}

export interface PromptOptions {
  title?: string;
  message?: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel?: string;
}

interface ConfirmRequest {
  kind: "confirm";
  id: number;
  title: string;
  message: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (v: boolean) => void;
}

interface PromptRequest {
  kind: "prompt";
  id: number;
  title: string;
  message: string;
  initialValue: string;
  placeholder: string;
  confirmLabel: string;
  resolve: (v: string | null) => void;
}

export type DialogRequest = ConfirmRequest | PromptRequest;

interface DialogBusState {
  current: DialogRequest | null;
  nextId: number;
  listeners: Set<() => void>;
}

function state(): DialogBusState {
  return getSharedState<DialogBusState>("__dialogBusState", () => ({
    current: null,
    nextId: 1,
    listeners: new Set(),
  }));
}

function notify(): void {
  state().listeners.forEach((fn) => fn());
}

export function subscribeDialog(fn: () => void): () => void {
  state().listeners.add(fn);
  return () => state().listeners.delete(fn);
}

export function getCurrentDialog(): DialogRequest | null {
  return state().current;
}

export function requestConfirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const s = state();
    s.current = {
      kind: "confirm",
      id: s.nextId++,
      title: opts.title || "Are you sure?",
      message: opts.message,
      confirmLabel: opts.confirmLabel || "Confirm",
      danger: opts.danger !== false,
      resolve,
    };
    notify();
  });
}

export function requestPrompt(opts: PromptOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const s = state();
    s.current = {
      kind: "prompt",
      id: s.nextId++,
      title: opts.title || "",
      message: opts.message || "",
      initialValue: opts.initialValue || "",
      placeholder: opts.placeholder || "",
      confirmLabel: opts.confirmLabel || "Save",
      resolve,
    };
    notify();
  });
}

export function resolveConfirm(value: boolean): void {
  const s = state();
  if (!s.current || s.current.kind !== "confirm") return;
  s.current.resolve(value);
  s.current = null;
  notify();
}

export function resolvePrompt(value: string | null): void {
  const s = state();
  if (!s.current || s.current.kind !== "prompt") return;
  s.current.resolve(value);
  s.current = null;
  notify();
}
